import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../config";

interface Notification {
  id: string; // Laravel utilise des UUIDs pour les notifications
  type: string;
  data: {
    title?: string;
    message?: string;
    type?: "booking" | "payment" | "client" | "passport";
  };
  read_at: string | null;
  created_at: string;
}

interface AdminHeaderProps {
  toggleSidebar?: () => void;
  onLogout?: () => void;
}

export const AdminHeader: React.FC<AdminHeaderProps> = ({ toggleSidebar, onLogout }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const notificationRef = useRef<HTMLDivElement>(null);

  // Charger les notifications depuis l'API Laravel
  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem("token_admin_silverjarz") || localStorage.getItem("token_admin");
      const response = await axios.get(`${API_URL}/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Laravel renvoie toutes les notifications et unread_notifications
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_notifications?.length || 0);
    } catch (error) {
      console.error("Erreur lors du chargement des notifications:", error);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // Optionnel : Actualiser toutes les 30 secondes
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fermeture des menus au clic à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setIsNotificationsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Marquer une notification spécifique comme lue au clic
  const handleMarkAsRead = async (id: string, readAt: string | null) => {
    if (readAt) return; // Déjà lue
    try {
      const token = localStorage.getItem("token_admin_silverjarz") || localStorage.getItem("token_admin");
      await axios.patch(`${API_URL}/notifications/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Mettre à jour l'état local
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Erreur lors du marquage de la notification:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const token = localStorage.getItem("token_admin_silverjarz") || localStorage.getItem("token_admin");
      await axios.post(`${API_URL}/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setNotifications((prev) => prev.map((n) => ({ ...n, read_at: new Date().toISOString() })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Erreur lors du marquage de toutes les notifications:", error);
    }
  };

  const getNotificationIcon = (type?: string) => {
    switch (type) {
      case "booking":
        return "calendar-check";
      case "payment":
        return "credit-card";
      case "client":
        return "user-plus";
      default:
        return "bell";
    }
  };

  return (
    <header className="admin-header">
      <div className="header-container">
        {/* Left Section */}
        <div className="header-left">
          <button className="header-toggle" onClick={toggleSidebar} aria-label="Toggle sidebar">
            <i className="fas fa-bars"></i>
          </button>
          <div className="header-brand d-none d-md-flex">
            <span className="brand-dot"></span>
            <span className="brand-text">Dashboard</span>
          </div>
        </div>

        {/* Center - Search Bar */}
        <div className="header-center d-lg-flex">
          <div className="search-wrapper">
            <i className="fas fa-search search-icon"></i>
            <input
              type="text"
              className="search-input"
              placeholder="Search clients, bookings, trips..."
            />
            <span className="search-shortcut">⌘K</span>
          </div>
        </div>

        {/* Right Section */}
        <div className="header-right">
          {/* Notifications Dropdown */}
          <div className="header-action" ref={notificationRef}>
            <button
              className="action-btn notification-btn"
              onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
              aria-label="Notifications"
            >
              <i className="fas fa-bell"></i>
              {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
            </button>

            {isNotificationsOpen && (
              <div className="dropdown-menu notification-dropdown show" style={{ width: "320px", maxHeight: "400px", overflowY: "auto" }}>
                <div className="dropdown-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 15px" }}>
                  <span className="dropdown-title" style={{ fontWeight: "bold" }}>Notifications</span>
                  {unreadCount > 0 && (
                    <button className="dropdown-action" onClick={handleMarkAllAsRead} style={{ background: "none", border: "none", color: "#d97706", cursor: "pointer", fontSize: "12px" }}>
                      Mark all as read
                    </button>
                  )}
                </div>
                <div className="dropdown-body">
                  {notifications.length === 0 ? (
                    <div style={{ padding: "20px", textAlign: "center", color: "#6b7280", fontSize: "13px" }}>
                      No notifications found
                    </div>
                  ) : (
                    notifications.slice(0, 5).map((notif) => {
                      const isUnread = !notif.read_at;
                      const title = notif.data?.title || notif.data?.message || "Nouvelle notification";
                      const type = notif.data?.type;

                      return (
                        <div
                          key={notif.id}
                          onClick={() => handleMarkAsRead(notif.id, notif.read_at)}
                          className={`notification-item ${isUnread ? "unread" : ""}`}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "10px 15px",
                            borderBottom: "1px solid #f3f4f6",
                            backgroundColor: isUnread ? "#fdfbf7" : "#fff",
                            cursor: "pointer",
                          }}
                        >
                          <div className="notification-icon" style={{ marginRight: "12px", color: "#294b5a" }}>
                            <i className={`fas fa-${getNotificationIcon(type)}`}></i>
                          </div>
                          <div className="notification-content" style={{ flex: 1 }}>
                            <div className="notification-title" style={{ fontSize: "13px", color: "#1f2937", fontWeight: isUnread ? "600" : "400" }}>
                              {title}
                            </div>
                            <div className="notification-time" style={{ fontSize: "11px", color: "#9ca3af" }}>
                              {new Date(notif.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </div>
                          {isUnread && <span className="notification-dot" style={{ width: "8px", height: "8px", backgroundColor: "#d97706", borderRadius: "50%" }}></span>}
                        </div>
                      );
                    })
                  )}
                </div>
                <div className="dropdown-footer" style={{ padding: "10px", textAlign: "center", borderTop: "1px solid #f3f4f6" }}>
                  <Link to="/admin/notifications" className="dropdown-link" style={{ fontSize: "12px", color: "#294b5a", fontWeight: "600", textDecoration: "none" }} onClick={() => setIsNotificationsOpen(false)}>
                    View all notifications
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Messages */}
          <div className="header-action d-none d-sm-flex">
            <button className="action-btn" aria-label="Messages">
              <i className="fas fa-envelope"></i>
              <span className="action-badge">5</span>
            </button>
          </div>

          {/* Profile Dropdown */}
          <div className="header-action" ref={dropdownRef}>
            <button
              className="profile-btn"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              aria-label="User menu"
            >
              <div className="profile-avatar">
                <img
                  src="/img/avatar-admin.jpg"
                  alt="Admin"
                  className="avatar-image"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      "https://ui-avatars.com/api/?name=Admin&background=294b5a&color=fff&size=40";
                  }}
                />
                <span className="profile-status online"></span>
              </div>
              <div className="profile-info d-none d-md-block">
                <span className="profile-name">Admin</span>
                <span className="profile-role">Administrator</span>
              </div>
              <i className="fas fa-chevron-down profile-chevron"></i>
            </button>

            {isDropdownOpen && (
              <div className="dropdown-menu profile-dropdown show">
                <div className="dropdown-profile-header">
                  <div className="dropdown-profile-avatar">
                    <img
                      src="/img/avatar-admin.jpg"
                      alt="Admin"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          "https://ui-avatars.com/api/?name=Admin&background=294b5a&color=fff&size=40";
                      }}
                    />
                  </div>
                  <div className="dropdown-profile-info">
                    <div className="dropdown-profile-name">Admin</div>
                    <div className="dropdown-profile-email">admin@omantravel.com</div>
                  </div>
                </div>
                <div className="dropdown-divider"></div>
                <Link to="/admin/profile" className="dropdown-item">
                  <i className="fas fa-user"></i> My Profile
                </Link>
                <Link to="/admin/settings" className="dropdown-item">
                  <i className="fas fa-cog"></i> Settings
                </Link>
                <Link to="/admin/activity" className="dropdown-item">
                  <i className="fas fa-clock"></i> Activity Log
                </Link>
                <div className="dropdown-divider"></div>
                <button onClick={onLogout} className="dropdown-item logout-item">
                  <i className="fas fa-sign-out-alt"></i> Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default AdminHeader;